// Korean (한국어) dictionary. Implements MktDict — TypeScript enforces that it
// has exactly the same shape as the canonical English dictionary (en.ts).
// Tone: natural Korean marketing copy. The honest validator framing (no
// "never reaches users" overclaim) is preserved.

import type { MktDict } from "./en";

export const ko: MktDict = {
  nav: {
    items: {
      pricing: "요금",
      examples: "예시",
      templates: "라이브러리",
      design: "디자인",
      docs: "문서",
      developers: "개발자",
      blog: "블로그",
      about: "소개",
    },
    openStudio: "스튜디오 열기 →",
    themeToggle: "테마 전환",
    language: "언어",
    status: "상태",
    signIn: "로그인",
    menu: "메뉴",
  },

  footer: {
    tagline: "의도와 소스 자료를 살아있는 앱과 문서로 바꾸세요.",
    groups: {
      product: {
        title: "제품",
        links: {
          pricing: "요금",
          templates: "라이브러리",
          docs: "문서",
          designSystem: "디자인 시스템",
        },
      },
      company: {
        title: "회사",
        links: { about: "소개", blog: "블로그", contact: "문의" },
      },
      compare: {
        title: "비교",
        links: { framer: "vs Framer", wix: "vs Wix", v0: "vs V0" },
      },
      legal: {
        title: "법적 고지",
        links: { privacy: "개인정보", terms: "약관", security: "보안" },
      },
    },
    copyright: "coogo · PADO Software Design L.L.C.",
    location: "두바이, UAE",
  },

  homeFooter: {
    groups: {
      product: {
        title: "제품",
        links: {
          templates: "라이브러리",
          designSystem: "디자인 시스템",
          pricing: "요금",
          studio: "스튜디오",
          howItWorks: "작동 방식",
        },
      },
      resources: {
        title: "리소스",
        links: {
          documentation: "문서",
          blog: "블로그",
          apiReference: "API 레퍼런스",
          templates: "라이브러리",
        },
      },
      company: {
        title: "회사",
        links: { aboutUs: "회사 소개", contact: "문의", careers: "채용" },
      },
      legal: {
        title: "법적 고지",
        links: { terms: "약관", privacy: "개인정보", refund: "환불" },
      },
    },
  },

  hero: {
    badge: "coogo Studio",
    h1: {
      line1: "살아있는 애플리케이션.",
      line2: "살아있는 문서",
      accent: ".",
    },
    subhead:
      "무엇을 만들거나 바꾸고 싶은지 설명하세요. coogo는 소스 자료와 함께 작업하고, 보이는 변경안을 제안하며, 앱·문서·리포트를 검토, 공유, 내보내기 가능한 상태로 유지합니다.",
    promptLines: [
      "소스와 검토 메모, 내보내기 상태가 연결된 캠페인 리포트…",
      "병원 예약 앱 — 고객은 예약하고, 직원은 SMS 알림을 받고…",
      "월 구독과 다크 모드가 있는 멤버 전용 팟캐스트…",
      "추천 리더보드가 있는 캣시팅 서비스 대기자 명단…",
    ],
    inputPlaceholder:
      "온보딩 앱 만들기, 발표자료 변환, 리포트 수정처럼 coogo에게 요청하세요...",
    generate: "coogo에게 요청",
    chips: ["App", "Document", "Report", "검토 루프"],
  },

  why: {
    label: "01 / 제품",
    h2: "사용자 의도와 coogo agent가 함께 일하는 작업공간.",
    intro:
      "coogo는 정적인 페이지 생성기가 아닙니다. 의도와 소스 자료를 검토 가능하고, 수정 가능하고, 신뢰할 수 있는 산출물로 바꿉니다.",
    card1: {
      chip: "applications",
      h3: "애플리케이션은 계속 살아있습니다.",
      body: "제품 화면, 내부 도구, 모바일 업무 흐름을 요청하세요. coogo는 실행되는 앱을 Studio 중앙에 열어 후속 요청이 보이는 산출물 위에서 이어지게 합니다.",
      annLeft: "intent",
      annMid: "same artifact",
      annRight: "app",
    },
    card2: {
      chip: "documents",
      h3: "문서는 계속 편집 가능합니다.",
      body: "파일을 올리고 구조를 요청한 뒤, 조용히 덮어쓰는 AI가 아니라 제안과 승인 흐름으로 수정합니다. 문서는 일회성 초안이 아니라 작업 중인 산출물로 남습니다.",
    },
    card3: {
      chip: "reports",
      h3: "리포트는 소스와 연결됩니다.",
      body: "차트, 표, 텍스트, 내보내기가 소스 커버리지와 검증 확인에 연결되어, 공유 전에 신뢰 상태가 보입니다.",
      link: "시스템 보기 ->",
    },
  },

  how: {
    label: "02 / 흐름",
    h2: "요청하고, 검토하고, 승인하고, 내보냅니다.",
    intro:
      "기본 루프는 단순합니다. coogo에게 요청하고, 산출물을 확인하고, 제안된 변경을 결정한 뒤, 게시하거나 공유하거나 내보냅니다.",
    steps: [
      {
        n: "01",
        title: "coogo에게 요청",
        body: "원하는 결과를 설명하거나 소스 자료를 붙이세요. 요청은 URL에 노출되지 않고 Studio handoff로 이어집니다.",
        caption: "의도 저장",
      },
      {
        n: "02",
        title: "산출물 보기",
        body: "앱, 문서, 리포트가 중앙 작업면에 나타납니다. 소스와 확인 정보는 필요할 때 볼 수 있지만 작업물을 밀어내지 않습니다.",
        caption: "산출물 우선",
      },
      {
        n: "03",
        title: "결정",
        body: "coogo가 변경을 쉬운 말로 제안합니다. 산출물이 바뀌기 전에 사용, 수정 요청, 현재 유지 중 하나를 선택합니다.",
        caption: "사용 / 수정 / 유지",
      },
      {
        n: "04",
        title: "공유 또는 내보내기",
        body: "앱은 게시하고, 리뷰 링크는 공유하고, 문서와 리포트는 대상별 준비 상태와 검증 맥락을 확인하며 내보냅니다.",
        caption: "게시 / 공유 / 내보내기",
      },
    ],
  },

  templates: {
    label: "03 / 시작 경로",
    h2: "지금 가진 일의 종류에서 시작하세요.",
    intro:
      "coogo home은 Studio 안의 제작 언어와 같은 네 가지 경로로 방문자를 이어줍니다: application, document, report, revision.",
    all: "Library 열기",
    live: "준비됨",
    preview: "시작",
    webMobile: "coogo Studio",
    items: [
      { name: "애플리케이션 만들기", cat: "생성 앱 작업공간" },
      { name: "문서 변환하기", cat: "DOCX, HWPX, PPTX, XLSX" },
      { name: "리포트 만들기", cat: "소스 기반 표와 차트" },
      { name: "기존 작업 수정하기", cat: "제안과 승인 루프" },
    ],
  },

  builtOn: {
    label: "04 / 협업",
    h2: "사람과 agent가 같은 산출물 위에서 일합니다.",
    intro:
      "선 위주의 인터페이스는 사용자, 소스, coogo, 산출물, 검토 상태가 연결되어 있음을 보여줍니다. 내부 장치는 기본 화면에 노출하지 않습니다.",
    taglines: ["요청", "이해", "소스", "산출물", "검증"],
  },

  faq: {
    label: "05 / 질문",
    h2: "방문자가 가장 먼저 이해해야 할 것.",
    items: [
      {
        q: "coogo는 앱을 위한 도구인가요, 문서를 위한 도구인가요?",
        a: "둘 다입니다. 제품 약속은 살아있는 애플리케이션과 살아있는 문서입니다. coogo는 두 종류의 작업을 같은 Studio 루프에서 만들고, 바꾸고, 검토하고, 공유하고, 내보냅니다.",
      },
      {
        q: "랜딩 입력창에 쓰면 그 다음은 어떻게 되나요?",
        a: "coogo는 요청을 Studio handoff로 저장하고 작업공간을 엽니다. 프롬프트를 URL에 넣지 않고, 대화가 산출물과 연결되도록 이어갑니다.",
      },
      {
        q: "agent가 승인된 작업을 조용히 바꿀 수 있나요?",
        a: "아니요. 제작자용 기본 계약은 제안 우선입니다. coogo는 변경을 설명하고 대상을 보여준 뒤, 필요한 경우 사용, 수정, 현재 유지 결정을 기다립니다.",
      },
      {
        q: "확인 정보와 개발자 진단은 어디에 있나요?",
        a: "details, checks, history, developer diagnostics로 필요할 때 열 수 있습니다. 기본 화면은 산출물, 요청, 검토 결정, 전달 경로가 먼저입니다.",
      },
      {
        q: "네이티브 파일로 내보낼 수 있나요?",
        a: "네. 문서와 리포트 작업은 DOCX, HWPX, PPTX, XLSX, PDF 같은 대상 형식을 가질 수 있고, 준비 상태는 대상별로 보여줍니다.",
      },
      {
        q: "첫 화면은 무엇을 말해야 하나요?",
        a: "coogo가 대화와 보이는 산출물을 통해 애플리케이션, 문서, 리포트를 만들거나 바꿀 수 있다는 점입니다. 나머지는 그 이야기를 돕는 정보입니다.",
      },
    ],
  },

  cta: {
    label: "06 / 시작",
    h2: { pre: "살려내고 싶은 일을 ", accent: "coogo에게 가져오세요." },
    body: "요청, 소스 파일, 거친 리포트 아이디어에서 시작하세요. Studio는 그것을 산출물과 다음 결정으로 이어갑니다.",
    start: "Studio 열기",
    browse: "Library 둘러보기",
    chips: ["application", "document", "report", "approval loop"],
  },

  pricing: {
    label: "§ PRICING",
    h1: { line1: "하나의 가격으로,", line2: "살아있는 산출물을." },
    subhead: "앱, 문서, 리포트가 검토, 공유, 내보내기 흐름과 함께 제공됩니다.",
    billing: { monthly: "월간", annual: "연간 — 20% 할인" },
    perMonth: "/월",
    customPrice: "맞춤",
    contactForPricing: "가격 문의",
    billedAnnually: "연 단위 청구",
    billedMonthly: "월 단위 청구",
    tiers: [
      {
        name: "Hobby",
        tag: "평생 무료",
        cta: "무료로 시작",
        bullets: [
          "공유 산출물 1개",
          "cogo.xyz 서브도메인",
          "커뮤니티 지원",
          "100 MB 저장공간",
        ],
      },
      {
        name: "Creator",
        tag: "가장 인기",
        cta: "14일 무료 체험",
        bullets: [
          "공유 산출물 10개",
          "커스텀 도메인",
          "검토 워크플로",
          "5 GB 저장공간",
          "이메일 지원",
        ],
      },
      {
        name: "Studio",
        tag: "팀용",
        cta: "체험 시작",
        bullets: [
          "공유 산출물 50개",
          "팀 워크스페이스 (5석)",
          "우선 지원",
          "25 GB 저장공간",
          "고급 분석",
        ],
      },
      {
        name: "Enterprise",
        tag: "맞춤",
        cta: "문의하기",
        bullets: [
          "무제한 산출물",
          "SSO & SAML",
          "SLA",
          "전담 매니저",
          "맞춤 계약",
        ],
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "시작하기 전, 자주 묻는 질문",
    faq: [
      {
        q: "앱, 문서, 리포트가 같은 산출물로 계산되나요?",
        a: "네. 각 공유 산출물은 소스, 검토 상태, 내보내기 흐름을 함께 유지합니다.",
      },
      {
        q: "제 도메인을 쓸 수 있나요?",
        a: "Creator 이상이면 됩니다. 도메인을 coogo로 연결하면 SSL은 자동으로 처리됩니다.",
      },
      {
        q: "다운그레이드하면 어떻게 되나요?",
        a: "새 플랜을 초과하는 산출물은 삭제되지 않고 읽기 전용으로 전환됩니다. 언제든 업그레이드하면 다시 활성화됩니다.",
      },
      {
        q: "산출물 공유마다 추가 수수료가 있나요?",
        a: "아니요. 요금제는 공유 산출물, AI 크레딧, 저장공간, 팀 기능 기준입니다. 작업물에 대한 수수료를 떼지 않습니다.",
      },
    ],
  },

  about: {
    label: "§ ABOUT",
    h1: { line1: "coogo는 계속 바꿀 수 있는", line2: "작업을 만듭니다." },
    intro:
      "coogo는 하나의 제품 믿음에서 출발합니다. AI 결과물은 초안에서 멈추면 안 됩니다. 산출물은 살아있고, 보이고, 검토 가능하고, 출시 또는 내보내기 준비가 되어야 합니다.",
    timeline: [
      {
        year: "01",
        label: "의도",
        desc: "사용자가 coogo에게 실제 작업을 만들거나 바꾸라고 요청합니다.",
      },
      {
        year: "02",
        label: "산출물",
        desc: "앱, 문서, 리포트가 중앙 작업면이 됩니다.",
      },
      {
        year: "03",
        label: "결정",
        desc: "coogo가 제안하고, 사용자가 승인하고, 버전이 앞으로 갑니다.",
      },
    ],
    teamLabel: "§ TEAM",
    teamTitle: "agent와 함께 일하는 사람들을 위해",
    team: [
      { name: "Creator", role: "의도를 정하고 보이는 변경을 승인합니다" },
      { name: "coogo", role: "소스를 이해하고 다음 단계를 제안합니다" },
      { name: "Reviewer", role: "공유 또는 내보내기 전에 산출물을 확인합니다" },
      { name: "Operator", role: "신뢰, 준비 상태, 전달을 맞춥니다" },
    ],
    contactLabel: "§ CONTACT",
    contactTitle: "인사 전해요.",
    contactIntro:
      "언론, 파트너십, 그냥 이야기하고 싶을 때 — 모든 메일에 답합니다.",
    contactGeneral: "일반",
    contactPress: "언론",
    contactEnterprise: "엔터프라이즈",
    formName: "이름",
    formNamePlaceholder: "이름을 입력하세요",
    formEmail: "이메일",
    formEmailPlaceholder: "you@email.com",
    formMessage: "메시지",
    formMessagePlaceholder: "무엇을 만들고 계신지 알려주세요…",
    formSend: "메시지 보내기",
  },

  templatesPage: {
    label: "§ LIBRARY",
    h1: "살아있는 작업공간을 선택하세요.",
    subhead:
      "필요한 산출물에서 시작하세요: 애플리케이션, 문서, 리포트, 수정 루프.",
    cats: [
      "Application",
      "Document",
      "Report",
      "Revision",
      "Source packet",
      "Review",
      "Admin",
      "Productivity",
    ],
  },

  blog: {
    label: "§ JOURNAL",
    h1: "살아있는 소프트웨어 노트.",
    subhead: "agent, 산출물, 소스 기반 문서, 검토 루프에 관한 제품 노트.",
    featuredSuffix: "추천",
    featuredExcerpt:
      "첫 화면은 내부 장치를 설명하는 곳이 아닙니다. 사용자가 coogo에게 무엇을 만들거나 바꿀지 묻고, 산출물을 계속 보게 해야 합니다.",
    minRead: "분",
    posts: [
      { t: "살아있는 애플리케이션. 살아있는 문서.", tag: "Product" },
      { t: "coogo agent 협업 화면을 디자인하기", tag: "Design" },
      { t: "소스 기반 리포트에 검증이 필요한 이유", tag: "Trust" },
      { t: "승인 루프: 사용, 수정, 현재 유지", tag: "Workflow" },
      { t: "랜딩 프롬프트에서 Studio 산출물까지", tag: "Handoff" },
      { t: "내부 용어 없이 제품을 설명하는 콘텐츠 페이지", tag: "Positioning" },
    ],
  },

  docs: {
    searchPlaceholder: "문서 검색",
    sections: [
      {
        h: "시작하기",
        items: ["소개", "첫 산출물 만들기", "공유 또는 게시", "다음으로 갈 곳"],
      },
      {
        h: "산출물 모델",
        items: ["개요", "스키마 레퍼런스", "블록", "이벤트 & 플로우", "표현식"],
      },
      {
        h: "액션",
        items: ["개요", "실행 모드", "액션 카탈로그", "Validator 루프"],
      },
      {
        h: "STUDIO",
        items: ["개요", "대시보드", "공유", "IDE 워크스페이스", "테마 에디터"],
      },
      {
        h: "디자인 시스템",
        items: ["개요", "토큰", "소스 모델", "시스템 확장하기"],
      },
      {
        h: "고급 레퍼런스",
        items: [
          "디자인 시스템 페이지",
          "컴포넌트 (작업 중)",
          "소스 모델 (작업 중)",
          "생성 (작업 중)",
          "배포 (작업 중)",
          "API 레퍼런스 (작업 중)",
        ],
      },
    ],
    centerLabel: "시작하기",
    h1: "살아있는 산출물로 빌드하기",
    intro:
      "coogo는 사용자 의도와 소스 자료를 검토, 수정, 공유, 내보내기, 검증 가능한 애플리케이션, 문서, 리포트로 바꿉니다.",
    calloutTitle: "coogo가 처음이신가요?",
    calloutBody:
      " 구현 방식이 아니라 하고 싶은 일을 설명하는 것부터 시작하세요. Studio는 대화를 산출물에 붙여 이어갑니다.",
    threeTitle: "알아둘 세 가지",
    three: [
      {
        title: "산출물이 먼저입니다.",
        body: " 중앙 작업면은 내부 체크 대시보드가 아니라 앱, 문서, 리포트여야 합니다.",
      },
      {
        title: "coogo는 적용 전에 제안합니다.",
        body: " 변경은 사용, 수정, 현재 유지 결정을 가진 이해 가능한 제안이어야 합니다.",
      },
      {
        title: "신뢰 상태는 필요할 때 보입니다.",
        body: " 소스 커버리지, 준비 상태, 내보내기 차단 사유, 검증 결과는 사용자가 필요할 때 열 수 있습니다.",
      },
    ],
    quickStartTitle: "빠른 시작",
    quickStart: {
      c1: "# 1. 의도 저장",
      c2: "# 2. 산출물을 중앙에 두기",
      c3: "# 3. 결정하고 전달하기",
    },
    next: "다음",
    nextLink: "Studio 열기 ->",
    onThisPage: "이 페이지에서",
    toc: ["소개", "알아둘 세 가지", "빠른 시작"],
  },

  designSystem: {
    sidebar: {
      groups: { foundations: "기초", system: "시스템", contract: "계약" },
      items: {
        overview: "개요",
        philosophy: "철학",
        architecture: "아키텍처",
        tokens: "토큰",
        components: "컴포넌트",
        patterns: "패턴",
        rules: "Tailwind 규칙",
        ontology: "소스 모델",
        schema: "JSON Schema",
        prompt: "LLM 프롬프트",
        samples: "샘플",
      },
      source: "소스",
    },
    heroChip: "coogo 디자인 시스템 · v4",
    heroH1: { line1: "닫힌 어휘,", accent: "열린 표현." },
    heroSubtitle:
      "마케팅, IDE, 검토, 대시보드, 챗 — 모든 화면을 하나의 어휘로.",
    sections: {
      philosophy: {
        name: "철학",
        h2: "어휘는 닫고, 표현은 연다.",
        sub: "coogo DS의 제1원칙.",
      },
      architecture: {
        name: "아키텍처",
        h2: "하나의 소스, 여섯 개의 산출물.",
        sub: "YAML에서 편집. 나머지는 전부 생성.",
      },
      tokens: {
        name: "토큰",
        h2: "47개 토큰, 드리프트 0.",
        sub: "47개 토큰, 드리프트 0.",
      },
      components: {
        name: "컴포넌트",
        h2: "하나의 버튼, 세 개의 세계.",
        sub: "하나의 버튼, 세 개의 세계.",
      },
      patterns: {
        name: "패턴",
        h2: "10개 아키타입, 무한한 앱.",
        sub: "10개 아키타입, 무한한 앱.",
      },
      rules: {
        name: "Tailwind 규칙",
        h2: "뼈대는 className, 외형은 variant.",
        sub: "뼈대는 className, 외형은 variant.",
      },
      ontology: {
        name: "소스 모델",
        h2: "소스 사실과 확인 정보가 산출물에 붙어 있습니다.",
        sub: "개발자 세부 정보는 필요할 때 열고, 기본 제작 화면을 차지하지 않습니다.",
      },
      schema: {
        name: "JSON Schema",
        h2: "다섯 개의 파일이 하나의 블록을 선언한다.",
        sub: "다섯 개의 파일이 하나의 블록을 선언한다.",
      },
      prompt: {
        name: "LLM 프롬프트",
        h2: "브리프 → 생성 → 검증 → 재시도.",
        sub: "브리프 → 생성 → 검증 → 재시도.",
      },
      samples: {
        name: "샘플",
        h2: "하나의 어휘, 다섯 개의 블록.",
        sub: "같은 어휘, 다섯 개의 블록.",
      },
    },
  },

  pricingPage: {
    h1: "요금",
    subhead:
      "공유 가능한 앱, 문서, 리포트를 계속 살아 있게 운영하는 팀을 위해.",
    currencyNote: "모든 요금제는 UAE 디르함(AED)으로 청구됩니다.",
    startFree: "무료로 시작",
    subscribeMonthly: "월간 구독",
    subscribeYearly: "연간 구독",
    perMonth: "/ 월",
    perYear: "/ 년",
    billingMonthly: "월간",
    billingYearly: "연간",
    yearlySaveHint: "2개월 무료",
    yearlySaveLabel: "연 {amount} 절약 ({pct}% 할인)",
    reassureFree: "신용카드가 필요 없습니다",
    reassurePaid: "언제든 해지 가능 · 안전한 결제",
    errPayment: "결제에 실패했습니다",
    errNoUrl: "결제 URL을 받지 못했습니다",
    errNotAuth: "인증되지 않았습니다",
    plans: {
      free: {
        title: "무료",
        description: "추천 대상: 첫 공유 산출물을 검증하는 사용자.",
        features: [
          "공유 산출물 1개",
          "월 500 AI 크레딧",
          "기본 검토 이력",
          "표준 지원",
        ],
        badge: "",
      },
      creator: {
        title: "Creator",
        description: "추천 대상: 여러 앱, 문서, 리포트를 공유하는 팀.",
        features: [
          "공유 산출물 5개",
          "월 5,000 AI 크레딧",
          "검토 이력",
          "커스텀 도메인 지원",
          "기본 분석",
        ],
        badge: "가장 인기",
      },
      business: {
        title: "Business",
        description: "추천 대상: 많은 공유 산출물을 운영하는 조직.",
        features: [
          "공유 산출물 30개",
          "월 20,000 AI 크레딧",
          "팀 검토 이력",
          "고급 분석",
          "우선 지원",
        ],
        badge: "",
      },
    },
    faq: {
      title: "요금 관련 질문",
      items: [
        {
          q: "결제 통화는 무엇인가요?",
          a: "모든 요금제는 UAE 디르함(AED)으로 청구됩니다. 보이는 가격이 곧 청구 금액이며, 숨은 환전 비용은 없습니다.",
        },
        {
          q: "월간과 연간, 무엇이 다른가요?",
          a: "연간 결제 시 2개월이 무료입니다 — 월 단위 결제 대비 약 17% 할인. 위의 토글로 월간/연간을 전환할 수 있습니다.",
        },
        {
          q: "무료 체험이 있나요?",
          a: "기간 제한이 있는 체험은 없습니다. 무료 요금제는 신용카드 없이 계속 사용할 수 있어, 먼저 공유 산출물을 만들고 필요할 때만 업그레이드하면 됩니다.",
        },
        {
          q: "언제든 해지할 수 있나요?",
          a: "네. 계정 설정에서 언제든 해지할 수 있습니다 — 현재 결제 주기가 끝날 때까지 요금제가 유지되고 다음 주기에는 청구되지 않습니다. 구독은 환불되지 않습니다. 환불 및 취소 정책을 확인하세요.",
        },
        {
          q: "공유 산출물은 게시 후 어떻게 관리하나요?",
          a: "각 산출물은 소스 패킷, 검토 이력, 내보내기 상태를 함께 보관하므로 공유 후에도 팀이 계속 개선할 수 있습니다.",
        },
        {
          q: "제 도메인을 쓸 수 있나요?",
          a: "네. 공유 산출물은 기본적으로 무료 name.cogo.xyz 주소로 게시되며, 설정 패널에서 커스텀 도메인을 연결하고 DNS를 저희 엣지로 지정할 수 있습니다.",
        },
        {
          q: "AI 크레딧이란 무엇인가요?",
          a: "AI 크레딧은 앱, 문서, 리포트 생성과 편집 등 AI 작업에 사용하는 월간 사용량입니다. 요금제별로 매월 무료 500, Creator 5,000, Business 20,000 크레딧이 제공됩니다.",
        },
      ],
    },
  },

  consent: {
    title: "결제 동의",
    description: "진행하기 전에 정책을 확인하고 동의해 주세요.",
    agreeIntro: "",
    and1: "과 ",
    and2: "에 동의하며, ",
    suffix: "을 읽었음을 확인합니다.",
    termsLabel: "이용약관",
    privacyLabel: "개인정보처리방침",
    refundLabel: "환불 및 취소 정책",
    cancel: "취소",
    processing: "처리 중...",
    proceed: "결제 진행",
    close: "닫기",
  },
};
